import { useId, type CSSProperties } from 'react';

export type IconName =
  | 'oversight'
  | 'globe'
  | 'intel'
  | 'markets'
  | 'strategies'
  | 'mesh'
  | 'dashboards'
  | 'settings'
  | 'menu'
  | 'search'
  | 'lock'
  | 'unlock'
  | 'chevron'
  | 'close'
  | 'plus'
  | 'minus'
  | 'play'
  | 'pause'
  | 'warning'
  | 'grid-2d'
  | 'sphere-3d';

type Props = {
  name: IconName;
  color?: string;
  size?: number;
  active?: boolean;
  style?: CSSProperties;
};

const H = 1.45;
const M = 0.85;
const F = 0.4;

export function Icon({ name, color = 'currentColor', size = 15, active = false, style }: Props) {
  const uid = useId();
  const c = color;
  const a = active;
  const s = size;
  const nav = { width: s, height: s, viewBox: '0 0 24 24', fill: 'none', style } as const;

  switch (name) {
    case 'oversight': {
      const clipId = `adi-${uid}`;
      return (
        <svg {...nav}>
          <defs>
            <clipPath id={clipId}><circle cx="12" cy="12" r="9" /></clipPath>
          </defs>
          <circle cx="12" cy="12" r="10.5" stroke={c} strokeWidth={H} />
          <path d="M 3 10 A 9 9 0 0 1 21 10" stroke={c} strokeWidth={M} fill="none" />
          {[-60, -30, -10, 0, 10, 30, 60].map((ang, i) => {
            const rad = ((ang - 90) * Math.PI) / 180;
            const x1 = 12 + Math.cos(rad) * 9;
            const y1 = 12 + Math.sin(rad) * 9;
            const x2 = 12 + Math.cos(rad) * 10.2;
            const y2 = 12 + Math.sin(rad) * 10.2;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth={ang === 0 ? H : F} />;
          })}
          <g clipPath={`url(#${clipId})`}>
            <g style={a ? { animation: 'cur-bank 5.5s ease-in-out infinite', transformOrigin: '12px 12px' } : undefined}>
              <rect x="-3" y="12" width="30" height="12" fill={c} opacity={a ? 0.28 : 0.18} />
              <line x1="-3" y1="12" x2="27" y2="12" stroke={c} strokeWidth={H} />
              <line x1="9" y1="8.5" x2="15" y2="8.5" stroke={c} strokeWidth={F} />
              <line x1="9" y1="15.5" x2="15" y2="15.5" stroke={c} strokeWidth={F} />
              <line x1="10" y1="5" x2="14" y2="5" stroke={c} strokeWidth={F} />
              <line x1="10" y1="19" x2="14" y2="19" stroke={c} strokeWidth={F} />
            </g>
          </g>
          <line x1="7" y1="12" x2="10" y2="12" stroke={c} strokeWidth={H} strokeLinecap="round" />
          <line x1="14" y1="12" x2="17" y2="12" stroke={c} strokeWidth={H} strokeLinecap="round" />
          <circle cx="12" cy="12" r="0.7" fill={c} />
          <polygon points="12,2.5 11.2,3.5 12.8,3.5" fill={c} />
        </svg>
      );
    }

    case 'globe': {
      const clipId = `globe-${uid}`;
      /* 24-wide landmass strip. Copy A sits at x=0..24, copy B at x=-24..0.
         Pan animates translateX 0 → +24, so at end copy B has moved into
         copy A's original slot (seamless wrap when the animation loops).
         Continents are spread horizontally across the full 24 units to
         avoid long ocean stretches at any rotation. */
      const landmasses = (
        <g>
          {/* Greenland */}
          <path
            d="M 2.8 4.2 Q 4.8 3.6 6.2 4.4 Q 6.5 5.2 5.6 5.8 Q 3.8 5.8 2.8 5.2 Z"
            fill={c}
            fillOpacity={a ? 0.55 : 0.4}
          />
          {/* Iceland */}
          <path d="M 8.6 5.4 Q 9.4 5.2 9.7 5.6 Q 9.4 6.1 8.8 6.0 Q 8.5 5.7 8.6 5.4 Z" fill={c} fillOpacity={a ? 0.55 : 0.4} />
          {/* North America */}
          <path
            d="M 1.2 6.6 Q 2.6 6.0 4.4 6.0 Q 6.2 6.2 7.0 7.2 Q 7.4 8.2 6.6 9.2 Q 5.8 9.8 5.0 10.4 Q 5.0 10.8 5.4 11.0 L 5.2 11.6 Q 4.2 11.4 3.2 10.6 Q 1.6 9.6 1.0 8.4 Q 0.6 7.4 1.2 6.6 Z"
            fill={c}
            fillOpacity={a ? 0.7 : 0.55}
          />
          {/* Central America isthmus */}
          <path d="M 5.2 11.6 Q 5.6 12.2 5.4 12.8 Q 4.9 12.8 4.5 12.0 Z" fill={c} fillOpacity={a ? 0.65 : 0.5} />
          {/* South America */}
          <path
            d="M 5.0 12.8 Q 6.2 13.0 6.4 14.0 Q 6.6 15.0 6.2 16.2 Q 5.7 17.6 5.2 18.6 Q 4.8 19.2 4.4 18.6 Q 4.0 17.6 4.2 16.0 Q 4.4 14.6 4.6 13.4 Q 4.6 12.9 5.0 12.8 Z"
            fill={c}
            fillOpacity={a ? 0.7 : 0.55}
          />
          {/* Europe */}
          <path
            d="M 10.4 5.0 Q 12.2 4.6 13.8 5.0 Q 13.9 6.0 13.0 6.4 Q 11.6 6.8 10.2 6.4 Q 10.0 5.6 10.4 5.0 Z"
            fill={c}
            fillOpacity={a ? 0.7 : 0.55}
          />
          {/* Asia main */}
          <path
            d="M 13.6 4.8 Q 16.8 4.4 19.8 4.8 Q 21.8 5.2 22.2 6.2 Q 21.6 7.4 19.8 7.8 Q 17.8 8.2 16.0 7.8 Q 14.2 7.8 13.6 7.2 Q 13.1 6.0 13.6 4.8 Z"
            fill={c}
            fillOpacity={a ? 0.72 : 0.58}
          />
          {/* Arabia */}
          <path
            d="M 13.2 8.0 Q 14.8 8.2 15.0 9.2 Q 14.6 10.2 13.6 10.2 Q 12.6 9.8 12.6 8.8 Q 12.7 8.1 13.2 8.0 Z"
            fill={c}
            fillOpacity={a ? 0.68 : 0.52}
          />
          {/* India */}
          <path
            d="M 16.2 8.2 Q 17.0 8.4 16.8 9.4 Q 16.4 10.4 15.8 10.0 Q 15.6 9.0 16.2 8.2 Z"
            fill={c}
            fillOpacity={a ? 0.68 : 0.52}
          />
          {/* Africa - wide at Mediterranean, Horn pokes east, tapered Cape */}
          <path
            d="M 11.2 9.4 Q 12.8 9.1 14.4 9.3 Q 14.8 10.0 14.3 10.7 L 15.0 11.0 Q 14.6 11.7 13.9 12.0 Q 13.6 13.0 13.1 14.0 Q 12.5 15.2 12.0 16.1 Q 11.4 15.6 11.3 14.8 Q 11.0 13.6 10.9 12.5 Q 10.6 11.6 10.7 10.6 Q 10.8 9.8 11.2 9.4 Z"
            fill={c}
            fillOpacity={a ? 0.72 : 0.58}
          />
          {/* Madagascar */}
          <path d="M 15.0 13.2 Q 15.6 13.3 15.5 14.1 Q 15.1 14.6 14.8 14.1 Q 14.7 13.6 15.0 13.2 Z" fill={c} fillOpacity={a ? 0.6 : 0.45} />
          {/* SE Asia / Indonesia spray */}
          <path d="M 18.6 9.8 Q 19.8 9.6 20.2 10.2 Q 19.8 10.9 18.9 10.7 Q 18.4 10.2 18.6 9.8 Z" fill={c} fillOpacity={a ? 0.6 : 0.45} />
          <path d="M 20.2 10.8 Q 21.0 10.7 21.2 11.2 Q 20.8 11.6 20.3 11.4 Q 20.1 11.1 20.2 10.8 Z" fill={c} fillOpacity={a ? 0.55 : 0.4} />
          <path d="M 18.8 11.6 Q 19.6 11.5 19.8 12.1 Q 19.4 12.5 19.0 12.3 Q 18.7 11.9 18.8 11.6 Z" fill={c} fillOpacity={a ? 0.55 : 0.4} />
          {/* Philippines */}
          <path d="M 21.4 9.2 Q 22.0 9.3 21.9 9.9 Q 21.5 10.2 21.2 9.8 Q 21.2 9.4 21.4 9.2 Z" fill={c} fillOpacity={a ? 0.55 : 0.4} />
          {/* Japan */}
          <path d="M 22.6 7.2 Q 23.2 7.4 23.0 8.2 Q 22.6 8.6 22.2 8.1 Q 22.2 7.5 22.6 7.2 Z" fill={c} fillOpacity={a ? 0.55 : 0.4} />
          {/* Australia */}
          <path
            d="M 19.2 14.4 Q 21.6 14.0 22.8 14.8 Q 22.6 16.0 21.4 16.4 Q 19.6 16.6 18.8 15.6 Q 18.7 14.6 19.2 14.4 Z"
            fill={c}
            fillOpacity={a ? 0.7 : 0.55}
          />
          {/* New Zealand */}
          <path d="M 23.0 17.0 Q 23.6 17.1 23.4 17.8 Q 23.0 18.1 22.7 17.6 Q 22.8 17.2 23.0 17.0 Z" fill={c} fillOpacity={a ? 0.55 : 0.4} />
          {/* Antarctica sliver */}
          <path
            d="M 0.6 20.0 Q 6 19.4 12 19.4 Q 18 19.4 23.4 20.0 L 23.4 21.4 Q 18 20.8 12 20.8 Q 6 20.8 0.6 21.4 Z"
            fill={c}
            fillOpacity={a ? 0.5 : 0.36}
          />
        </g>
      );
      return (
        <svg {...nav}>
          <defs>
            <clipPath id={clipId}><circle cx="12" cy="12" r="10.2" /></clipPath>
          </defs>
          <circle cx="12" cy="12" r="10.5" stroke={c} strokeWidth={H} fill="none" />
          <g clipPath={`url(#${clipId})`}>
            {a ? (
              <g style={{ animation: 'cur-globe-pan 12s linear infinite' }}>
                {landmasses}
                <g transform="translate(-24 0)">{landmasses}</g>
              </g>
            ) : (
              landmasses
            )}
            <line x1="1.5" y1="12" x2="22.5" y2="12" stroke={c} strokeWidth={F} opacity="0.22" strokeDasharray="0.8 1.2" />
          </g>
        </svg>
      );
    }

    case 'intel': {
      const ex = 16.5;
      const ey = 12;
      const rays = [
        { dy: -3.2, op: 0.95, w: H },
        { dy: -1.2, op: 0.8, w: H },
        { dy: 0.6, op: 0.65, w: H },
        { dy: 2.6, op: 0.5, w: M },
      ];
      return (
        <svg {...nav}>
          <line x1="2" y1="12" x2="7.5" y2="12" stroke={c} strokeWidth={H} strokeLinecap="round">
            {a && <animate attributeName="opacity" values="0.5;1;0.5" dur="2.2s" repeatCount="indefinite" />}
          </line>
          <polygon
            points="12,4 20,18 4,18"
            stroke={c}
            strokeWidth={H}
            fill={a ? c : 'none'}
            fillOpacity={a ? 0.08 : 0}
            strokeLinejoin="round"
          />
          <line x1="7.5" y1="12" x2={ex} y2={ey} stroke={c} strokeWidth={F} opacity="0.55" />
          {rays.map((r, i) => (
            <line
              key={i}
              x1={ex}
              y1={ey}
              x2="22.5"
              y2={ey + r.dy}
              stroke={c}
              strokeWidth={r.w}
              opacity={r.op}
              strokeLinecap="round"
            >
              {a && (
                <animate
                  attributeName="opacity"
                  values={`0;${r.op};0`}
                  dur="2.2s"
                  begin={`${i * 0.15}s`}
                  repeatCount="indefinite"
                />
              )}
            </line>
          ))}
        </svg>
      );
    }

    case 'markets':
      return (
        <svg {...nav}>
          <line x1="2" y1="21" x2="22" y2="21" stroke={c} strokeWidth={H} />
          <line x1="2" y1="2.5" x2="2" y2="21" stroke={c} strokeWidth={M} />
          {[6, 11, 16].map((y) => (
            <line key={y} x1="1.5" y1={y} x2="2.5" y2={y} stroke={c} strokeWidth={F} />
          ))}
          <line x1="5" y1="8" x2="5" y2="17" stroke={c} strokeWidth={F} />
          <rect x="3.8" y="10" width="2.4" height="5" fill={c} opacity="0.75" />
          <line x1="8.5" y1="11" x2="8.5" y2="19" stroke={c} strokeWidth={F} />
          <rect x="7.3" y="13" width="2.4" height="5" stroke={c} strokeWidth={M} fill="var(--bg-0)" />
          <line x1="12" y1="6" x2="12" y2="16" stroke={c} strokeWidth={F} />
          <rect x="10.8" y="8" width="2.4" height="6" fill={c} opacity="0.75" />
          <line x1="15.5" y1="9" x2="15.5" y2="18" stroke={c} strokeWidth={F} />
          <rect x="14.3" y="11" width="2.4" height="6" stroke={c} strokeWidth={M} fill="var(--bg-0)" />
          <line x1="19" y1={a ? '3' : '5'} x2="19" y2="17" stroke={c} strokeWidth={M} />
          {a ? (
            <rect x="17.6" y="7" width="2.8" height="9" fill={c}>
              <animate attributeName="y" values="9;4;9" dur="2.6s" repeatCount="indefinite" />
              <animate attributeName="height" values="7;12;7" dur="2.6s" repeatCount="indefinite" />
            </rect>
          ) : (
            <rect x="17.6" y="7" width="2.8" height="7" fill={c} />
          )}
          {a && (
            <line x1="19" y1="5.5" x2="22" y2="5.5" stroke={c} strokeWidth={F} strokeDasharray="0.8 0.8" opacity="0.7">
              <animate attributeName="y1" values="7.5;4;7.5" dur="2.6s" repeatCount="indefinite" />
              <animate attributeName="y2" values="7.5;4;7.5" dur="2.6s" repeatCount="indefinite" />
            </line>
          )}
        </svg>
      );

    case 'strategies':
      return (
        <svg {...nav}>
          <circle cx="12" cy="12" r="10.5" stroke={c} strokeWidth={H} />
          <circle cx="12" cy="12" r="7" stroke={c} strokeWidth={F} opacity="0.5" />
          <circle cx="12" cy="12" r="3.5" stroke={c} strokeWidth={F} opacity="0.5" />
          <line x1="1.5" y1="12" x2="22.5" y2="12" stroke={c} strokeWidth={F} opacity="0.5" />
          <line x1="12" y1="1.5" x2="12" y2="22.5" stroke={c} strokeWidth={F} opacity="0.5" />
          <path d="M 12 12 L 12 2 A 10 10 0 0 1 20 7 Z" fill={c} opacity={a ? 0.28 : 0.15}>
            {a && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 12 12"
                to="360 12 12"
                dur="3.5s"
                repeatCount="indefinite"
              />
            )}
          </path>
          <circle cx="8" cy="7" r="0.9" fill={c} />
          <circle cx="15" cy="9" r="0.7" fill={c} opacity="0.8" />
          <circle cx="16" cy="15" r="0.6" fill={c} opacity="0.6" />
        </svg>
      );

    case 'mesh':
      return (
        <svg {...nav}>
          {[
            [4, 4],
            [20, 4],
            [4, 20],
            [20, 20],
            [12, 4],
            [12, 20],
            [4, 12],
            [20, 12],
          ].map(([x, y], i) => (
            <line key={i} x1={x} y1={y} x2="12" y2="12" stroke={c} strokeWidth={F} opacity="0.55" />
          ))}
          {[
            [4, 4],
            [20, 4],
            [4, 20],
            [20, 20],
          ].map(([x, y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r="1.9" stroke={c} strokeWidth={M} fill="var(--bg-0)" />
              <circle cx={x} cy={y} r="0.5" fill={c} />
            </g>
          ))}
          {[
            [12, 4],
            [12, 20],
            [4, 12],
            [20, 12],
          ].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="1.1" fill={c} opacity="0.7" />
          ))}
          <circle
            cx="12"
            cy="12"
            r="3.2"
            stroke={c}
            strokeWidth={H}
            fill={a ? c : 'var(--bg-0)'}
            fillOpacity={a ? 0.85 : 1}
          />
          <circle cx="12" cy="12" r="1.2" fill={a ? 'var(--bg-0)' : c} />
          {a &&
            [
              [4, 4],
              [20, 4],
              [4, 20],
              [20, 20],
            ].map(([x, y], i) => (
              <circle key={i} r="0.8" fill={c}>
                <animateMotion dur={`${2 + i * 0.3}s`} repeatCount="indefinite" path={`M${x} ${y} L12 12`} />
                <animate attributeName="opacity" values="0;1;0" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
              </circle>
            ))}
        </svg>
      );

    case 'dashboards':
      return (
        <svg {...nav}>
          <rect x="2" y="2" width="20" height="20" stroke={c} strokeWidth={H} />
          <rect x="3.5" y="3.5" width="17" height="7" stroke={c} strokeWidth={M} fill={c} fillOpacity="0.08" />
          <circle cx="7" cy="16" r="3.5" stroke={c} strokeWidth={M} fill="none" />
          {Array.from({ length: 8 }).map((_, i) => {
            const rad = (i * 45 * Math.PI) / 180;
            const x1 = 7 + Math.sin(rad) * 2.7;
            const y1 = 16 - Math.cos(rad) * 2.7;
            const x2 = 7 + Math.sin(rad) * 3.5;
            const y2 = 16 - Math.cos(rad) * 3.5;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth={F} />;
          })}
          <g style={a ? { animation: 'cur-sway 3.2s ease-in-out infinite', transformOrigin: '7px 16px' } : undefined}>
            <line
              x1="7"
              y1="16"
              x2={a ? '8.8' : '5.2'}
              y2={a ? '14.3' : '14.8'}
              stroke={c}
              strokeWidth={M}
              strokeLinecap="round"
            />
          </g>
          <circle cx="7" cy="16" r="0.6" fill={c} />
          <rect x="13" y="13" width="3.5" height="2.2" stroke={c} strokeWidth={M} fill={a ? c : 'none'} />
          <rect x="17" y="13" width="3.5" height="2.2" stroke={c} strokeWidth={M} fill="none" />
          <rect x="13" y="16" width="3.5" height="2.2" stroke={c} strokeWidth={M} fill="none" />
          <rect x="17" y="16" width="3.5" height="2.2" stroke={c} strokeWidth={M} fill="none" />
          <circle cx="14" cy="20" r="0.8" stroke={c} strokeWidth={M} fill={c} />
          <circle cx="19" cy="20" r="0.8" stroke={c} strokeWidth={M} fill="var(--bg-0)" />
        </svg>
      );

    case 'settings': {
      const rows = [
        { y: 6, kx: 8, dur: 3.2, from: 8, to: 14 },
        { y: 12, kx: 15, dur: 3.8, from: 15, to: 9 },
        { y: 18, kx: 10, dur: 4.4, from: 10, to: 17 },
      ];
      return (
        <svg {...nav}>
          {rows.map((r, i) => (
            <g key={i}>
              <line
                x1="3"
                y1={r.y}
                x2="21"
                y2={r.y}
                stroke={c}
                strokeWidth={M}
                strokeLinecap="round"
                opacity="0.45"
              />
              <circle cx={r.kx} cy={r.y} r="2" stroke={c} strokeWidth={H} fill={a ? c : 'var(--bg-0)'}>
                {a && (
                  <animate
                    attributeName="cx"
                    values={`${r.kx};${r.to};${r.from};${r.kx}`}
                    dur={`${r.dur}s`}
                    repeatCount="indefinite"
                  />
                )}
              </circle>
            </g>
          ))}
        </svg>
      );
    }

    case 'menu':
      return (
        <svg {...nav}>
          <line x1="4" y1="7" x2="20" y2="7" stroke={c} strokeWidth={H} strokeLinecap="round" />
          <line x1="4" y1="12" x2="20" y2="12" stroke={c} strokeWidth={H} strokeLinecap="round" />
          <line x1="4" y1="17" x2="15" y2="17" stroke={c} strokeWidth={H} strokeLinecap="round" />
        </svg>
      );

    // --- UI icons (unchanged, 14x14 grid) ---
    case 'search':
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" fill="none" style={style}>
          <circle cx="5" cy="5" r="3.3" stroke={c} />
          <line x1="7.5" y1="7.5" x2="11" y2="11" stroke={c} />
        </svg>
      );
    case 'lock':
      return (
        <svg width={s} height={s} viewBox="0 0 12 14" fill="none" style={style}>
          <rect x="1.5" y="6" width="9" height="7" stroke={c} strokeWidth="1.1" />
          <path d="M3 6 V4 a3 3 0 0 1 6 0 V6" stroke={c} strokeWidth="1.1" fill="none" />
        </svg>
      );
    case 'unlock':
      return (
        <svg width={s} height={s} viewBox="0 0 12 14" fill="none" style={style}>
          <rect x="1.5" y="6" width="9" height="7" stroke={c} strokeWidth="1.1" />
          <path d="M3 6 V4.2 a3 3 0 0 1 5.4 -1.5" stroke={c} strokeWidth="1.1" fill="none" strokeLinecap="round" />
        </svg>
      );
    case 'chevron':
      return (
        <svg width={s} height={s} viewBox="0 0 10 10" fill="none" style={style}>
          <path d="M1.5 3.5 L5 7 L8.5 3.5" stroke={c} strokeWidth="1.2" fill="none" />
        </svg>
      );
    case 'close':
      return (
        <svg width={s} height={s} viewBox="0 0 10 10" fill="none" style={style}>
          <line x1="1" y1="1" x2="9" y2="9" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
          <line x1="9" y1="1" x2="1" y2="9" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    case 'plus':
      return (
        <svg width={s} height={s} viewBox="0 0 10 10" fill="none" style={style}>
          <line x1="5" y1="1.5" x2="5" y2="8.5" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
          <line x1="1.5" y1="5" x2="8.5" y2="5" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    case 'minus':
      return (
        <svg width={s} height={s} viewBox="0 0 10 10" fill="none" style={style}>
          <line x1="1.5" y1="5" x2="8.5" y2="5" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    case 'play':
      return (
        <svg width={s} height={s} viewBox="0 0 10 10" fill="none" style={style}>
          <path d="M2 1.5 L9 5 L2 8.5 Z" fill={c} stroke="none" />
        </svg>
      );
    case 'pause':
      return (
        <svg width={s} height={s} viewBox="0 0 10 10" fill="none" style={style}>
          <rect x="2" y="2" width="2" height="6" fill={c} />
          <rect x="6" y="2" width="2" height="6" fill={c} />
        </svg>
      );
    case 'warning':
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" fill="none" style={style}>
          <path d="M7 1 L13 12 L1 12 Z" stroke={c} strokeWidth="1.2" fill="none" strokeLinejoin="round" />
          <line x1="7" y1="5.5" x2="7" y2="8.5" stroke={c} strokeWidth="1.2" strokeLinecap="round" />
          <circle cx="7" cy="10.5" r="0.7" fill={c} />
        </svg>
      );
    case 'grid-2d':
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" fill="none" style={style}>
          <rect x="1.5" y="1.5" width="11" height="11" stroke={c} fill="none" />
          <line x1="5.5" y1="1.5" x2="5.5" y2="12.5" stroke={c} />
          <line x1="9" y1="1.5" x2="9" y2="12.5" stroke={c} />
          <line x1="1.5" y1="5.5" x2="12.5" y2="5.5" stroke={c} />
          <line x1="1.5" y1="9" x2="12.5" y2="9" stroke={c} />
        </svg>
      );
    case 'sphere-3d':
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" fill="none" style={style}>
          <circle cx="7" cy="7" r="5.5" stroke={c} fill="none" />
          <circle cx="7" cy="7" r="2" fill={c} />
        </svg>
      );
  }
}
